import { useEffect, useMemo, useState, type SVGProps } from "react";
import { assetUrl } from "../lib/assets";
import { trackEvent } from "../lib/analytics";

interface EarlyAccessLandingPageProps {
  launchAtIso?: string;
  onJoinWaitlist: () => void;
}

interface CountdownParts {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const FALLBACK_LAUNCH_ISO = "2026-04-11T00:00:00-08:00";

function parseLaunchDate(launchAtIso?: string): Date {
  const parsed = new Date(launchAtIso || FALLBACK_LAUNCH_ISO);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }
  return new Date(FALLBACK_LAUNCH_ISO);
}

function computeCountdown(target: Date): CountdownParts {
  const remaining = Math.max(0, target.getTime() - Date.now());
  const totalMinutes = Math.floor(remaining / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const seconds = Math.floor((remaining % 60000) / 1000);
  return {
    totalMs: remaining,
    days,
    hours,
    minutes,
    seconds,
  };
}

function IconGlobe(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14.5 14.5 0 0 1 0 18" />
      <path d="M12 3a14.5 14.5 0 0 0 0 18" />
    </svg>
  );
}

function IconArrow(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function IconMail(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h12a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 18 19H6a2.5 2.5 0 0 1-2.5-2.5v-9Z" />
      <path d="m4.5 8.5 7.5 5 7.5-5" />
    </svg>
  );
}

function IconLock(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8.5 10.5V8a3.5 3.5 0 1 1 7 0v2.5" />
    </svg>
  );
}

function IconShield(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3.5 5.5 6v5.9c0 4.3 2.7 7.8 6.5 8.6 3.8-.8 6.5-4.3 6.5-8.6V6L12 3.5Z" />
      <path d="m9.2 11.8 2.1 2.1 3.6-3.6" />
    </svg>
  );
}

function formatLaunchDate(target: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(target);
}

function formatLaunchDateShort(target: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(target);
}

export function EarlyAccessLandingPage({ launchAtIso, onJoinWaitlist }: EarlyAccessLandingPageProps) {
  const launchDate = useMemo(() => parseLaunchDate(launchAtIso), [launchAtIso]);
  const [countdown, setCountdown] = useState<CountdownParts>(() => computeCountdown(launchDate));
  const [isRedirecting, setIsRedirecting] = useState(false);
  const hasLaunched = countdown.totalMs <= 0;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCountdown(computeCountdown(launchDate));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [launchDate]);

  function handleJoinWaitlist(): void {
    trackEvent("early_access_join_clicked", { has_launched: hasLaunched });
    setIsRedirecting(true);
    onJoinWaitlist();
  }

  return (
    <div data-testid="early-access-landing" className="prx-onboarding-auth">
      <img
        src={assetUrl("pantry-inventory-bg.png")}
        alt=""
        className="prx-onboarding-auth__bg-image"
        aria-hidden="true"
      />
      <div className="prx-onboarding-auth__bg-tone" aria-hidden="true" />
      <div className="prx-onboarding-auth__bg-glow" aria-hidden="true" />
      <div className="prx-onboarding-auth__vignette" aria-hidden="true" />
      <div className="prx-onboarding-auth__bg-grain" aria-hidden="true" />

      <div className="prx-onboarding-auth__inner">
        <article className="prx-onboarding-auth__card">
          <div className="prx-onboarding-auth__card-top">
            <div className="prx-onboarding-auth__brand">
              <img src={assetUrl("app-icon-logo.png")} alt="PantryRx" className="prx-onboarding-auth__brand-icon" />
              <span className="prx-onboarding-auth__brand-text">PantryRx</span>
            </div>
            <span className="prx-onboarding-auth__language" aria-label="Language and locale">
              <IconGlobe className="prx-onboarding-auth__language-icon" aria-hidden="true" />
              <span>US &middot; EN</span>
            </span>
          </div>

          <header className="prx-onboarding-auth__headline">
            <h1>Early Access List</h1>
            <p>
              {hasLaunched ? "iOS launched on " : "Launching iOS on "}
              <span>{formatLaunchDateShort(launchDate)}</span>
            </p>
            <p className="prx-onboarding-auth__launch-copy">
              {hasLaunched
                ? "PantryRx is live. Sign in with Google to continue."
                : "Join now for first-wave access before launch day."}
            </p>
          </header>

          <section className="prx-onboarding-auth__countdown" aria-live="polite">
            <div className="prx-onboarding-auth__countdown-grid">
              <div className="prx-onboarding-auth__countdown-item">
                <strong>{countdown.days}</strong>
                <span>Days</span>
              </div>
              <div className="prx-onboarding-auth__countdown-item">
                <strong>{countdown.hours}</strong>
                <span>Hours</span>
              </div>
              <div className="prx-onboarding-auth__countdown-item">
                <strong>{countdown.minutes}</strong>
                <span>Minutes</span>
              </div>
              <div className="prx-onboarding-auth__countdown-item">
                <strong>{countdown.seconds}</strong>
                <span>Seconds</span>
              </div>
            </div>
            <p className="prx-onboarding-auth__countdown-note">
              {!hasLaunched
                ? `Time left until launch day on ${formatLaunchDate(launchDate)}.`
                : "Launch day has arrived. Sign in with Google to continue."}
            </p>
          </section>

          <div className="prx-onboarding-auth__actions">
            <p className="prx-onboarding-auth__join-copy">
              Use Google to sign in or sign up. We will add your account to the early access list automatically.
            </p>
            <button
              type="button"
              data-testid="early-access-join"
              className="prx-onboarding-auth__button prx-onboarding-auth__button--primary prx-onboarding-auth__button--pulse"
              disabled={isRedirecting}
              onClick={handleJoinWaitlist}
            >
              <span className="prx-onboarding-auth__button-icon" aria-hidden="true">
                <IconMail />
              </span>
              <span>{isRedirecting ? "Redirecting..." : "Join Waitlist with Google"}</span>
              <span className="prx-onboarding-auth__button-arrow" aria-hidden="true">
                <IconArrow />
              </span>
            </button>
          </div>

          <footer className="prx-onboarding-auth__security">
            <IconLock className="prx-onboarding-auth__security-icon" aria-hidden="true" />
            <span>Secure early access notifications</span>
            <IconShield className="prx-onboarding-auth__security-icon" aria-hidden="true" />
          </footer>
        </article>
      </div>
    </div>
  );
}
